import { IconButton } from '@chakra-ui/button';
import Icon from '@chakra-ui/icon';
import { Box, Flex, Link } from '@chakra-ui/layout';
import { useBreakpointValue } from '@chakra-ui/media-query';
import { MenuButton, MenuDivider } from '@chakra-ui/menu';
import { Fade } from '@chakra-ui/transition';
import firebase from 'firebase';
import React from 'react';
import {
  Calendar,
  Layers,
  LogOut,
  Menu as MenuIcon,
  Trello,
  User,
} from 'react-feather';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useCurrentUserData } from '../../hooks/useCurrentUserData';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { Logo } from '../Logo';
import { Menu, MenuItem } from '../styled-chakra/Menu';

const userRoutes = [
  { route: '/upcoming-courses', label: 'Kursplan', icon: Calendar },
  { route: '/current-week', label: 'Aktuelle Woche', icon: Trello },
];

const adminRoutes = [
  ...userRoutes,
  { route: '/admin', label: 'Admin-Portal', icon: Layers },
];

const isActive = (
  active: typeof userRoutes[0] | undefined,
  route: typeof userRoutes[0]
) => active && active.route === route.route;

interface DesktopMenuProps {
  activeRoute: typeof userRoutes[0] | undefined;
  routes: typeof userRoutes;
}

const DesktopMenu: React.FC<DesktopMenuProps> = ({ activeRoute, routes }) => (
  <Flex bg="white" w="full">
    <RouterLink to="/">
      <Logo height="2.2em" width="auto" pr={5} />
    </RouterLink>
    {routes.map((route) => (
      <Flex key={route.route} align="center" px={5}>
        <Fade in={isActive(activeRoute, route)}>
          <Box
            w="4px"
            h="4px"
            bg={isActive(activeRoute, route) ? 'brand.500' : 'transparent'}
            borderRadius="full"
            mr={1}
          />
        </Fade>
        <Icon
          mr={1}
          boxSize="1em"
          as={route.icon}
          color={isActive(activeRoute, route) ? 'brand.500' : 'black'}
        />
        <Link
          fontWeight="bold"
          as={RouterLink}
          to={route.route}
          color={isActive(activeRoute, route) ? 'brand.500' : 'black'}
        >
          {route.label}
        </Link>
      </Flex>
    ))}
  </Flex>
);

interface MobileMenuProps {
  activeRoute: typeof userRoutes[0] | undefined;
  routes: typeof userRoutes;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ activeRoute, routes }) => (
  <Flex w="full" align="center">
    <Menu
      triggerComponent={
        <MenuButton
          size="md"
          borderRadius="xl"
          as={IconButton}
          colorScheme="brand"
          icon={<Icon as={MenuIcon} />}
        />
      }
    >
      {routes.map((route, index) => (
        <div key={route.route}>
          <MenuItem
            fontWeight={isActive(activeRoute, route) ? 'bold' : 'normal'}
            key={route.route}
            as={RouterLink}
            to={route.route}
            icon={<Icon as={route.icon} />}
          >
            {route.label}
          </MenuItem>
          {index !== routes.length - 1 && <MenuDivider />}
        </div>
      ))}
    </Menu>
    <Logo height="1.8em" width="auto" pl={4} />
  </Flex>
);

interface HeaderProps {}

export const Header: React.FC<HeaderProps> = () => {
  const isMobile = useBreakpointValue([true, true, false]);
  const history = useHistory();
  const currentUser = useCurrentUser();
  const currentUserData = useCurrentUserData();

  const routes = currentUserData?.isAdmin ? adminRoutes : userRoutes;

  const activeRoute = routes
    .concat({ label: 'Mein Profil', route: '/profile', icon: User })
    .find((route) => route.route === history.location.pathname);
  useDocumentTitle(activeRoute?.label);

  const signOut = () => {
    firebase.auth().signOut();
  };

  return (
    <Flex
      justify="space-between"
      align="center"
      m="0 auto"
      w="full"
      h="100px"
      boxShadow="md"
      bg="white"
    >
      <Flex align="center" w="full" maxW="1200px" m="0 auto" py={6} px={[3, 6]}>
        {isMobile ? (
          <MobileMenu activeRoute={activeRoute} routes={routes} />
        ) : (
          <DesktopMenu activeRoute={activeRoute} routes={routes} />
        )}
        {currentUser && (
          <Menu
            triggerComponent={
              <MenuButton
                size="md"
                as={IconButton}
                colorScheme="brand"
                borderRadius="xl"
                icon={<Icon as={User} />}
              />
            }
          >
            <MenuItem as={RouterLink} to="/profile" icon={<Icon as={User} />}>
              Profil ({currentUser.email})
            </MenuItem>
            <MenuDivider />
            <MenuItem onClick={signOut} icon={<Icon as={LogOut} />}>
              Logout
            </MenuItem>
          </Menu>
        )}
      </Flex>
    </Flex>
  );
};
